// /account/profile — "Mi cuenta · Perfil". The person's own record:
// personal data (view -> edit -> save the display name), profile photo, and
// active sessions. The name saves through PATCH /account/me; sessions close via
// DELETE /account/sessions/:id + POST /account/sessions/revoke-others. The photo
// ships DB-backed (PR 19): POST/DELETE /account/avatar. Email is the login
// identity (changing it is a separate verified flow); language/timezone are
// display-only here. Data via the getAccount() seam -> GET /account/me + /sessions.

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { type ChangeEvent, type ReactNode, useRef, useState } from 'react'

import {
  type AccountSession,
  getAccount,
  removeAvatar,
  requestEmailChange,
  revokeOtherSessions,
  revokeSession,
  saveAccountProfile,
  uploadAvatar,
} from './account-data.js'
import { AccountTabsHeader } from './AccountTabsHeader.js'
import { useAuthStore } from '../../auth/store.js'
import {
  Avatar,
  Button,
  FormField,
  Input,
  Modal,
  PasswordInput,
  Spinner,
  toast,
  ViewEditCard,
  type ViewEditField,
} from '../../design-system/index.js'
import { apiErrorMessage } from '../../lib/api-error.js'
import { QueryError } from '../../lib/QueryError.js'
import { useAvatar } from '../../lib/useAvatar.js'

const MAX_AVATAR_BYTES = 2 * 1024 * 1024
const ACCEPTED_AVATAR_MIMES = new Set(['image/jpeg', 'image/png', 'image/webp'])
// Client-side pre-check only — the API is still the source of truth
// (invalid_secondary_email / invalid_email), this just avoids a round trip
// for the obvious case.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

function DesktopIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[18px]"
    >
      <rect x="2.5" y="4" width="19" height="13" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  )
}

function MobileIcon(): JSX.Element {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      className="size-[18px]"
    >
      <rect x="5" y="2" width="14" height="20" rx="2.6" />
      <path d="M11 18h2" />
    </svg>
  )
}

function SessionRow({
  session,
  onRevoke,
  busy,
}: {
  session: AccountSession
  onRevoke: () => void
  busy: boolean
}): JSX.Element {
  return (
    <li className="flex items-center gap-3.5 border-b border-line px-4 py-3 last:border-b-0">
      <span
        aria-hidden
        className="grid size-[38px] flex-none place-items-center rounded-[9px] bg-field text-text-2"
      >
        {session.kind === 'mobile' ? <MobileIcon /> : <DesktopIcon />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-2 text-[12.5px] font-semibold text-text">
          {session.device}
          {session.current && (
            <span className="rounded-[5px] bg-ok-bg px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.04em] text-ok">
              Este dispositivo
            </span>
          )}
        </span>
        <span className="mt-0.5 block text-[11px] text-text-3">
          {session.location} · {session.lastActive}
        </span>
      </span>
      {!session.current && (
        <Button variant="secondary" size="sm" disabled={busy} onClick={onRevoke}>
          Cerrar sesión
        </Button>
      )}
    </li>
  )
}

function PanelCard({
  title,
  sub,
  children,
  footer,
}: {
  title: string
  sub?: string
  children: ReactNode
  footer?: ReactNode
}): JSX.Element {
  return (
    <section className="rounded-xl border border-line bg-surface">
      <div className="border-b border-line px-4 py-3.5">
        <h2 className="text-[13.5px] font-bold text-text">{title}</h2>
        {sub && <p className="mt-0.5 text-[12px] leading-snug text-text-2">{sub}</p>}
      </div>
      {children}
      {footer}
    </section>
  )
}

function ChangeEmailModal({
  open,
  onClose,
  currentEmail,
}: {
  open: boolean
  onClose: () => void
  currentEmail: string
}): JSX.Element {
  const [phase, setPhase] = useState<'form' | 'confirm'>('form')
  const [newEmail, setNewEmail] = useState('')
  const [currentPassword, setCurrentPassword] = useState('')
  const [busy, setBusy] = useState(false)

  function reset(): void {
    setPhase('form')
    setNewEmail('')
    setCurrentPassword('')
  }

  function close(): void {
    reset()
    onClose()
  }

  async function onSubmit(): Promise<void> {
    setBusy(true)
    try {
      await requestEmailChange({ new_email: newEmail, current_password: currentPassword })
      close()
      toast.ok('Te enviamos un enlace de confirmación a tu nuevo correo.')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo iniciar el cambio de correo.'))
    } finally {
      setBusy(false)
    }
  }

  const newEmailTrimmed = newEmail.trim()
  const newEmailValid = newEmailTrimmed !== '' && EMAIL_RE.test(newEmailTrimmed)

  return (
    <Modal
      open={open}
      onClose={close}
      title="Cambiar correo electrónico"
      subtitle={`Correo actual: ${currentEmail}`}
      busy={busy}
      footer={
        phase === 'form' ? (
          <>
            <Button variant="secondary" onClick={close} disabled={busy}>
              Cancelar
            </Button>
            <Button
              onClick={() => setPhase('confirm')}
              disabled={!newEmailValid || currentPassword.length === 0}
            >
              Continuar
            </Button>
          </>
        ) : (
          <>
            <Button variant="secondary" onClick={() => setPhase('form')} disabled={busy}>
              Volver
            </Button>
            <Button onClick={() => void onSubmit()} loading={busy}>
              Cambiar correo
            </Button>
          </>
        )
      }
    >
      {phase === 'form' ? (
        <div className="flex flex-col gap-3.5">
          <FormField
            label="Correo nuevo"
            htmlFor="new-email"
            {...(newEmailTrimmed !== '' && !newEmailValid
              ? { error: 'Escribe un correo válido.' }
              : {})}
          >
            <Input
              type="email"
              inputMode="email"
              autoComplete="email"
              required
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              disabled={busy}
              placeholder="tu-nuevo-correo@ejemplo.com"
            />
          </FormField>
          <FormField label="Confirma tu contraseña" htmlFor="current-password-for-email">
            <PasswordInput
              autoComplete="current-password"
              required
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              disabled={busy}
            />
          </FormField>
        </div>
      ) : (
        <p className="text-[12.5px] leading-snug text-text-2">
          ¿Seguro que quieres cambiar tu correo de acceso a{' '}
          <b className="font-semibold text-text">{newEmail}</b>? Te enviaremos un enlace de
          confirmación y tu correo actual seguirá activo hasta que lo confirmes.
        </p>
      )}
    </Modal>
  )
}

export function AccountProfilePage(): JSX.Element {
  const qc = useQueryClient()
  const selfId = useAuthStore((s) => s.user?.id)
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['account'],
    queryFn: getAccount,
  })
  const [busyId, setBusyId] = useState('')
  const [emailModalOpen, setEmailModalOpen] = useState(false)
  const [photoBusy, setPhotoBusy] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const avatarUrl = useAvatar(selfId, data?.profile.hasAvatar)

  async function afterPhotoChange(): Promise<void> {
    await qc.invalidateQueries({ queryKey: ['account'] })
    if (selfId !== undefined) {
      await qc.invalidateQueries({ queryKey: ['avatar', selfId] })
    }
  }

  async function onPickPhoto(e: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return
    if (file.size > MAX_AVATAR_BYTES) {
      toast.error('La imagen debe pesar menos de 2 MB.')
      return
    }
    if (!ACCEPTED_AVATAR_MIMES.has(file.type)) {
      toast.error('Formato no soportado. Usa JPEG, PNG o WebP.')
      return
    }
    setPhotoBusy(true)
    try {
      await uploadAvatar(file)
      await afterPhotoChange()
      toast.ok('Foto actualizada')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo actualizar la foto.'))
    } finally {
      setPhotoBusy(false)
    }
  }

  async function onRemovePhoto(): Promise<void> {
    setPhotoBusy(true)
    try {
      await removeAvatar()
      await afterPhotoChange()
      toast.ok('Foto eliminada')
    } catch (err) {
      toast.error(apiErrorMessage(err, 'No se pudo quitar la foto.'))
    } finally {
      setPhotoBusy(false)
    }
  }

  async function onSave(patch: Record<string, string>): Promise<void> {
    const update: { name?: string; secondary_email?: string } = {}
    if (patch.name !== undefined) update.name = patch.name
    if (patch.secondary_email !== undefined) update.secondary_email = patch.secondary_email
    if (update.name === undefined && update.secondary_email === undefined) return
    if (
      update.secondary_email !== undefined &&
      update.secondary_email !== '' &&
      !EMAIL_RE.test(update.secondary_email)
    ) {
      throw new Error('Escribe un correo válido o deja el campo vacío.')
    }
    await saveAccountProfile(update)
    await qc.invalidateQueries({ queryKey: ['account'] })
    toast.ok('Perfil actualizado')
  }

  async function closeSession(id: string): Promise<void> {
    setBusyId(id)
    try {
      await revokeSession(id)
      await qc.invalidateQueries({ queryKey: ['account'] })
      toast.ok('Sesión cerrada')
    } catch (err) {
      toast.error('No se pudo cerrar la sesión', { description: apiErrorMessage(err, '') })
    } finally {
      setBusyId('')
    }
  }

  async function closeOthers(): Promise<void> {
    setBusyId('others')
    try {
      await revokeOtherSessions()
      await qc.invalidateQueries({ queryKey: ['account'] })
      toast.ok('Cerramos las demás sesiones')
    } catch (err) {
      toast.error('No se pudo completar', { description: apiErrorMessage(err, '') })
    } finally {
      setBusyId('')
    }
  }

  if (isError) {
    return (
      <div data-testid="account-profile" className="mx-auto w-full max-w-4xl py-10">
        <QueryError message="No pudimos cargar tu cuenta." onRetry={() => void refetch()} />
      </div>
    )
  }
  if (isLoading || !data) {
    return (
      <div data-testid="account-profile" className="mx-auto w-full max-w-4xl py-10">
        <Spinner label="Cargando tu cuenta…" />
      </div>
    )
  }

  const { profile, sessions } = data

  // Name and secondary email are editable here. Primary email is the login
  // identity (changed via a separate verified flow); language/timezone are
  // display-only for now.
  const fields: ViewEditField[] = [
    { key: 'name', label: 'Nombre completo', value: profile.name, editable: true },
    { key: 'email', label: 'Correo electrónico', value: profile.email, editable: false },
    {
      key: 'secondary_email',
      label: 'Correo secundario',
      value: profile.secondaryEmail ?? '',
      editable: true,
      ...(profile.secondaryEmailStatus === 'pending'
        ? { helper: 'Pendiente de confirmación. Revisa la bandeja de ese correo.' }
        : {}),
    },
    { key: 'language', label: 'Idioma', value: profile.language, editable: false },
    { key: 'timezone', label: 'Zona horaria', value: profile.timezone, editable: false },
  ]

  return (
    <div data-testid="account-profile" className="mx-auto w-full max-w-4xl">
      <AccountTabsHeader active="perfil" />

      <div className="mt-5 grid grid-cols-1 items-start gap-4 lg:grid-cols-[1.7fr,1fr]">
        <div>
          <ViewEditCard title="Datos personales" fields={fields} canEdit onSave={onSave} />
          <div className="mt-2 flex justify-end">
            <Button variant="secondary" size="sm" onClick={() => setEmailModalOpen(true)}>
              Cambiar correo
            </Button>
          </div>
        </div>

        {/* photo card */}
        <div className="flex flex-col items-center rounded-xl border border-line bg-surface px-5 py-5 text-center">
          <Avatar name={profile.name} src={avatarUrl} size={54} variant="staff" hideName />
          <div className="mt-3 text-[14px] font-bold text-text">{profile.name}</div>
          <div className="mt-0.5 text-[11.5px] text-text-2">
            {profile.roleLabel} · {profile.workspaceName}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={(e) => void onPickPhoto(e)}
          />
          <div className="mt-3.5 flex items-center gap-2">
            <Button
              variant="secondary"
              size="sm"
              disabled={photoBusy}
              onClick={() => fileInputRef.current?.click()}
            >
              Cambiar foto
            </Button>
            {profile.hasAvatar && (
              <Button
                variant="ghost"
                size="sm"
                disabled={photoBusy}
                className="!text-sens hover:!bg-sens-bg"
                onClick={() => void onRemovePhoto()}
              >
                Quitar foto
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* active sessions */}
      <div className="mt-4">
        <PanelCard
          title="Sesiones activas"
          sub="Los dispositivos donde tu cuenta tiene sesión abierta ahora mismo."
          footer={
            sessions.some((s) => !s.current) ? (
              <div className="flex border-t border-line px-4 py-3">
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busyId === 'others'}
                  className="ml-auto !text-warn hover:!bg-warn-bg"
                  onClick={() => void closeOthers()}
                >
                  Cerrar todas las demás sesiones
                </Button>
              </div>
            ) : null
          }
        >
          <ul className="flex flex-col">
            {sessions.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                busy={busyId === s.id}
                onRevoke={() => void closeSession(s.id)}
              />
            ))}
          </ul>
        </PanelCard>
      </div>

      <ChangeEmailModal
        open={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        currentEmail={profile.email}
      />
    </div>
  )
}
